import { useCallback, useRef, useState } from "react";
import {
  Button,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorFooter,
  EntitySelectorFooterAction,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
} from "@salestrak/ui";

import { UserSelector } from "#app/components/entity-selectors";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";

type TeamAddMemberActionProps = {
  teamId: string;
  /** Every current member (leads AND members), excluded from the picker's
   *  pool so the same person can never be added twice. */
  existingUserIds: string[];
  /**
   * The element that opens the picker. Defaults to the filled 24px "Add
   * member" button the page chrome wears; a table cell passes the slot-sized
   * `EntityGroupAddSlot` instead. Only the trigger's face changes — one
   * action, one commit path, whatever it looks like.
   */
  trigger?: React.ReactElement;
  /** Placement of the picker popup against its trigger. Default "end" suits a
   *  right-aligned header action; a cell mid-table wants "start". */
  align?: "start" | "center" | "end";
};

/**
 * The team's one "Add member" action: a trigger opening the user picker,
 * whose footer hands off to the invite dialog. Checked members
 * commit as ONE mutation when the picker closes, so a multi-pick is one
 * write and one toast. The mutation refetches the roster query itself, so
 * this mounts anywhere on a team's chrome — the team page masthead (every
 * tab) and the settings roster header — without a callback into whichever
 * table happens to be showing.
 *
 * Render only for `Team.viewerCanManage` holders: the server-computed gate
 * the membership mutations enforce, so the button never promises a write
 * the server rejects.
 */
export function TeamAddMemberAction({
  teamId,
  existingUserIds,
  trigger,
  align = "end",
}: TeamAddMemberActionProps) {
  const { addTeamMembers } = useTeamMutations();
  const canInvite = useCanInvite();

  const pendingMemberIds = useRef<string[]>([]);
  const handlePendingChange = useCallback((ids: string[]) => {
    pendingMemberIds.current = ids;
  }, []);

  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const handleAddOpenChange = useCallback(
    async (open: boolean) => {
      setAddOpen(open);
      if (open) return;
      const ids = pendingMemberIds.current;
      pendingMemberIds.current = [];
      if (ids.length === 0) return;
      await addTeamMembers(teamId, ids, "member", "team_add_action");
    },
    [teamId, addTeamMembers],
  );

  // Closing through the shared handler first commits any members already
  // checked in the picker, so switching to invite never drops them.
  const handleInviteFromSelector = useCallback(() => {
    void handleAddOpenChange(false);
    setInviteOpen(true);
  }, [handleAddOpenChange]);

  return (
    <>
      <UserSelector
        value={[]}
        onValueChange={handlePendingChange}
        open={addOpen}
        onOpenChange={handleAddOpenChange}
        exclude={existingUserIds}
      >
        <EntitySelectorTrigger
          render={
            trigger ?? (
              // Pill: every top-right primary action (Create Team, Invite,
              // Create Workflow) stands on the stadium, and this one matches.
              <Button size="xs">
                {/* Raw string: a bare Text element pins 14px into the 24px
                  * rung; a string child inherits the button's 12px font. */}
                Add Member
              </Button>
            )
          }
        />
        <EntitySelectorContent size="card" align={align}>
          <EntitySelectorInput />
          <EntitySelectorPool value="users" />
          <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
          {canInvite ? (
            <EntitySelectorFooter>
              <EntitySelectorFooterAction
                icon="mail"
                onClick={handleInviteFromSelector}
              >
                Invite by email
              </EntitySelectorFooterAction>
            </EntitySelectorFooter>
          ) : null}
        </EntitySelectorContent>
      </UserSelector>

      {canInvite ? (
        <InviteMembersDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          defaultTeamIds={[teamId]}
        />
      ) : null}
    </>
  );
}
